import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, 
  Medal, 
  Crown, 
  TrendingUp, 
  Users, 
  Calendar,
  Filter,
  RefreshCw,
  Award,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import WinnerAnnouncement from '../components/WinnerAnnouncement';

const BACKEND_URL = window.location.origin;

const PublicLeaderboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState('all');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [stats, setStats] = useState({
    totalPlayers: 0,
    totalGames: 0,
    activePlayers: 0
  });

  useEffect(() => {
    fetchGames();
    fetchLeaderboard();
  }, [selectedGame]);

  // Auto-refresh leaderboard every 2 seconds during competition
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchLeaderboard();
      setLastRefresh(new Date());
    }, 2000); // Refresh every 2 seconds (ultra-fast real-time updates!)
    
    return () => clearInterval(interval);
  }, [autoRefresh, selectedGame]);

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/games/modes`);
      setGames(response.data.all_games || []);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      if (selectedGame === 'all') {
        // Fetch overall leaderboard (all games combined)
        const response = await axios.get(`${BACKEND_URL}/api/admin/leaderboard`);
        setLeaderboard(response.data.leaderboard || []);
        
        // Calculate stats
        const uniquePlayers = new Set(response.data.leaderboard.map(entry => entry.player_id)).size;
        setStats({
          totalPlayers: uniquePlayers,
          totalGames: response.data.leaderboard.length,
          activePlayers: uniquePlayers
        });
      } else {
        // Fetch game-specific leaderboard
        const response = await axios.get(`${BACKEND_URL}/api/leaderboard/${selectedGame}`);
        setLeaderboard(response.data || []);
        
        setStats({
          totalPlayers: response.data.length,
          totalGames: response.data.length,
          activePlayers: response.data.length
        });
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getGameIcon = (gameId) => {
    const icons = {
      'tap-challenge': '👆',
      'memory-puzzle': '🧩',
      'lightning-quiz': '⚡',
      'reaction-test': '⏱️',
      'flappy-tap': '🐦',
      'ball-balance': '⚖️',
      'target-hit': '🎯',
      'timing-tap': '⏰',
      'ball-jump': '📈',
      'bottle-flip': '🍾',
      'tap-flash': '⚡'
    };
    return icons[gameId] || '🎮';
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500 text-black';
    if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-600 text-black';
    return 'bg-gray-800 text-white';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Trophy className="w-6 h-6 text-orange-400" />;
    return <span className="text-lg font-bold">#{rank}</span>;
  };

  const formatScore = (score) => {
    return score?.toLocaleString() || '0';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
      {/* Winner Announcement Banner - Compact */}
      <WinnerAnnouncement compact={true} />
      
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-yellow-600 p-4 rounded-full">
              <Trophy className="w-12 h-12 text-black" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
            🏆 Global Leaderboard
          </h1>
          <p className="text-gray-400 text-lg">
            Top players competing for amazing gadgets and prizes!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border-blue-700/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Players</p>
                  <p className="text-3xl font-bold text-blue-400">{stats.totalPlayers}</p>
                </div>
                <Users className="w-12 h-12 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/40 to-green-800/20 border-green-700/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Games Played</p>
                  <p className="text-3xl font-bold text-green-400">{stats.totalGames}</p>
                </div>
                <Zap className="w-12 h-12 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/20 border-yellow-700/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Active Today</p>
                  <p className="text-3xl font-bold text-yellow-400">{stats.activePlayers}</p>
                </div>
                <TrendingUp className="w-12 h-12 text-yellow-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live Update Controls */}
        <Card className="bg-gradient-to-r from-green-900/30 to-blue-900/30 border-green-700/50 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${autoRefresh ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
                <div>
                  <p className="text-white font-semibold text-sm sm:text-base">
                    {autoRefresh ? '🔴 LIVE' : '⏸️ Paused'}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {autoRefresh ? 'Updates every 2 seconds ⚡' : 'Auto-refresh paused'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <p className="text-gray-400 text-xs">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </p>
                <Button
                  onClick={() => {
                    fetchLeaderboard();
                    setLastRefresh(new Date());
                    toast.success('Leaderboard refreshed!');
                  }}
                  variant="outline"
                  size="sm"
                  className="border-green-700/50 text-green-400 hover:bg-green-900/20"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Now
                </Button>
                <Button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  variant="outline"
                  size="sm"
                  className={`border-gray-700 ${autoRefresh ? 'text-yellow-400' : 'text-gray-400'}`}
                >
                  {autoRefresh ? 'Pause' : 'Resume'} Auto-Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filter Section */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8">
          <CardHeader>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-yellow-400" />
                <CardTitle className="text-xl">Filter by Game</CardTitle>
              </div>
              <Button
                onClick={fetchLeaderboard}
                variant="outline"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border-gray-700"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <button
                onClick={() => setSelectedGame('all')}
                className={`p-3 rounded-lg transition-all font-semibold ${
                  selectedGame === 'all'
                    ? 'bg-yellow-600 text-black'
                    : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                All Games
              </button>
              {games.map((game) => (
                <button
                  key={game.game_id}
                  onClick={() => setSelectedGame(game.game_id)}
                  className={`p-3 rounded-lg transition-all ${
                    selectedGame === game.game_id
                      ? 'bg-yellow-600 text-black font-semibold'
                      : 'bg-gray-800 text-white hover:bg-gray-700'
                  }`}
                >
                  <div className="text-2xl mb-1">{getGameIcon(game.game_id)}</div>
                  <div className="text-xs">{game.game_name}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard Table */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Award className="w-6 h-6 text-yellow-400" />
              {selectedGame === 'all' ? 'All Games Rankings' : 'Game Rankings'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-20">
                <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No players yet. Be the first to compete!</p>
                <Button
                  onClick={() => navigate('/play-games')}
                  className="mt-6 bg-yellow-600 hover:bg-yellow-700 text-black font-bold"
                >
                  Play Now
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left p-4 text-gray-400">Rank</th>
                      <th className="text-left p-4 text-gray-400">Player</th>
                      {selectedGame === 'all' && <th className="text-left p-4 text-gray-400">Game</th>}
                      <th className="text-right p-4 text-gray-400">Score</th>
                      <th className="text-right p-4 text-gray-400">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((entry, index) => {
                      const rank = index + 1;
                      return (
                        <tr
                          key={`${entry.player_id}-${entry.game_id}-${index}`}
                          className={`border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors ${
                            rank <= 3 ? 'bg-gradient-to-r from-gray-800/50 to-transparent' : ''
                          }`}
                        >
                          <td className="p-4">
                            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getRankColor(rank)}`}>
                              {getRankIcon(rank)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="font-semibold text-white">
                                {entry.player_name || entry.email || 'Anonymous'}
                              </p>
                              <p className="text-sm text-gray-400">{entry.player_id?.substring(0, 8)}...</p>
                            </div>
                          </td>
                          {selectedGame === 'all' && (
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{getGameIcon(entry.game_id)}</span>
                                <span className="text-sm text-gray-300">{entry.game_name}</span>
                              </div>
                            </td>
                          )}
                          <td className="p-4 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-xl font-bold text-yellow-400">
                                {formatScore(entry.score)}
                              </span>
                              <span className="text-xs text-gray-500">points</span>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2 text-gray-400">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">{formatDate(entry.submission_time || entry.timestamp)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-700/50">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to Compete?</h3>
              <p className="text-gray-300 mb-6">
                Join our gaming community and compete for amazing gadgets and prizes!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/play-games')}
                  className="bg-yellow-600 hover:bg-yellow-700 text-black font-bold px-8 py-6 text-lg"
                >
                  <Trophy className="w-5 h-5 mr-2" />
                  Play Now
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  variant="outline"
                  className="border-yellow-600 text-yellow-400 hover:bg-yellow-600/10 px-8 py-6 text-lg"
                >
                  Sign Up Free
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PublicLeaderboard;
